import type { TagDto } from '@crm/shared'
import { ErroRequisicao } from '../api/cliente'
import { useCriarTag } from '../api/hooks-leads'
import { usePerguntar } from './perguntar'

/**
 * Etiqueta criada na hora, de onde ela é marcada (ADR 0069): a janela de
 * etiquetas da conversa e as fichas do contato e da empresa.
 *
 * Criar ali ficava fora de propósito, "para a lista não encher de variações do
 * mesmo nome". A proteção agora é esta conta: o "Criar" só aparece quando não
 * existe etiqueta com o mesmo nome, sem diferença de maiúscula, acento ou
 * espaço sobrando.
 */

/** Cores de dado (regra 16): uma por vez, para etiquetas novas não saírem todas iguais. */
const CORES = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#64748b']

export function corDaNovaEtiqueta(quantasJaExistem: number): string {
  return CORES[quantasJaExistem % CORES.length] as string
}

function normalizar(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
}

/** A etiqueta que já tem este nome, se houver: "Matrícula " e "matricula" são a mesma. */
export function etiquetaDeMesmoNome<T extends { nome: string }>(etiquetas: T[], nome: string): T | undefined {
  const procurado = normalizar(nome)
  return procurado ? etiquetas.find((t) => normalizar(t.nome) === procurado) : undefined
}

/**
 * O "+ Nova etiqueta" das fichas do contato e da empresa: pergunta o nome, cria
 * e devolve a etiqueta para a ficha marcar. Nome que já existe não cria outra:
 * devolve a que existe, e ela é que fica marcada.
 */
export function BotaoNovaEtiqueta({
  etiquetas,
  aoCriar,
}: {
  etiquetas: TagDto[]
  aoCriar: (etiqueta: TagDto) => void
}) {
  const { pedirTexto, dialogo } = usePerguntar()
  const criar = useCriarTag()

  async function perguntar() {
    const nome = (await pedirTexto('Nova etiqueta', '', 'Nome da etiqueta'))?.trim()
    if (!nome) return
    const existente = etiquetaDeMesmoNome(etiquetas, nome)
    if (existente) {
      aoCriar(existente)
      return
    }
    criar.mutate({ nome, cor: corDaNovaEtiqueta(etiquetas.length) }, { onSuccess: aoCriar })
  }

  return (
    <>
      <button
        type="button"
        onClick={() => void perguntar()}
        disabled={criar.isPending}
        className="rounded-md border border-dashed border-tinta-300 px-2 py-1 text-xs font-medium text-tinta-500 transition hover:border-marca/50 hover:text-marca disabled:opacity-60"
      >
        {criar.isPending ? 'Criando…' : '+ Nova etiqueta'}
      </button>
      {criar.error instanceof ErroRequisicao ? (
        <span className="text-xs text-red-700">{criar.error.corpo.mensagem}</span>
      ) : null}
      {dialogo}
    </>
  )
}
